import { Link } from "react-router-dom";
import { ChevronDown, ChevronUp, HelpCircle, Shield, Eye, Users, FileText, Lock } from "lucide-react";
import { useState } from "react";
import Header from "../components/Header";
import Footer from "../components/Footer";
import SEO from '../components/SEO';

const FAQ_ITEMS = [
  {
    category: "Allgemein",
    icon: HelpCircle,
    color: "#A0E8AF",
    questions: [
      {
        q: "Was ist mobbing-melden.de?",
        a: "Wir sind eine kostenlose Plattform, wo du Mobbing, Cybermobbing oder Identitätsdiebstahl melden kannst. Wir helfen dir und leiten bei schweren Fällen die Informationen an die richtigen Stellen weiter."
      },
      {
        q: "Ist das hier wirklich kostenlos?",
        a: "Ja, absolut! Du musst nichts bezahlen. Wir finanzieren uns durch Spenden und wollen, dass jedes Kind Hilfe bekommen kann."
      },
      {
        q: "Für wen ist diese Seite?",
        a: "Für alle Kinder und Jugendliche, die Mobbing erleben oder erlebt haben. Auch Eltern und Lehrer können sich hier informieren."
      },
    ]
  },
  {
    category: "Meldung",
    icon: FileText,
    color: "#BDE0FE",
    questions: [
      {
        q: "Muss ich meinen Namen angeben?",
        a: "Nein! Du kannst komplett anonym bleiben. Nur wenn du möchtest, dass wir dir antworten können, kannst du freiwillig deine E-Mail angeben."
      },
      {
        q: "Was passiert mit meiner Meldung?",
        a: "Unser Team liest jede Meldung sorgfältig durch. Wir prüfen den Fall und entscheiden, wie wir am besten helfen können. Bei schweren Fällen können wir die Informationen (mit deiner Zustimmung, wenn du Kontaktdaten angegeben hast) an die Polizei weitergeben."
      },
      {
        q: "Kann ich Bilder und Videos hochladen?",
        a: "Ja! Screenshots, Fotos oder Videos sind sehr hilfreich als Beweis. Du kannst Dateien bis zu 50 MB hochladen."
      },
      {
        q: "Wie lange dauert es, bis sich jemand meldet?",
        a: "Wenn du Kontaktdaten angegeben hast, versuchen wir innerhalb von 1-3 Tagen zu antworten. Bei dringenden Fällen schneller."
      },
    ]
  },
  {
    category: "Sicherheit & Datenschutz",
    icon: Lock,
    color: "#FFC4D6",
    questions: [
      {
        q: "Wer kann meine Meldung sehen?",
        a: "Nur unser kleines, geschultes Team. Wir geben deine Daten nicht an Dritte weiter, außer du stimmst zu oder es liegt eine akute Gefahr vor."
      },
      {
        q: "Erfahren meine Eltern davon?",
        a: "Nicht automatisch. Wenn du anonym meldest, wissen wir ja nicht mal, wer du bist. Aber: Bei sehr ernsten Fällen (z.B. wenn du in Gefahr bist) müssen wir vielleicht Hilfe holen."
      },
      {
        q: "Kann ich Ärger bekommen, wenn ich melde?",
        a: "Nein! Es ist mutig und richtig, Mobbing zu melden. Du tust nichts Falsches. Im Gegenteil - du hilfst vielleicht auch anderen."
      },
      {
        q: "Was ist mit meinen hochgeladenen Dateien?",
        a: "Die werden sicher auf unseren Servern gespeichert. Nur unser Team kann sie sehen. Wenn ein Fall abgeschlossen ist, werden die Daten nach einer bestimmten Zeit gelöscht."
      },
    ]
  },
  {
    category: "Nach der Meldung",
    icon: Users,
    color: "#F6E05E",
    questions: [
      {
        q: "Was passiert mit den Mobbern?",
        a: "Das hängt vom Fall ab. Bei Cybermobbing können Accounts gesperrt werden. Bei schweren Fällen kann die Polizei eingeschaltet werden. Mobbing ist kein Kavaliersdelikt!"
      },
      {
        q: "Kann ich mehrere Meldungen machen?",
        a: "Ja, wenn du verschiedene Vorfälle melden möchtest oder neue Informationen hast."
      },
      {
        q: "Was, wenn das Mobbing nicht aufhört?",
        a: "Dann melde es erneut! Sprich auch mit Erwachsenen, denen du vertraust. Du kannst auch die Notfall-Hotlines anrufen."
      },
    ]
  },
];


const FAQ_SCHEMA = {
  '@context': 'https://schema.org',
  '@type': 'FAQPage',
  'mainEntity': FAQ_ITEMS.flatMap(cat =>
    cat.questions.map(q => ({
      '@type': 'Question',
      'name': q.q,
      'acceptedAnswer': { '@type': 'Answer', 'text': q.a }
    }))
  )
};

export default function FAQ() {
  const [openItems, setOpenItems] = useState({});

  const toggleItem = (categoryIndex, questionIndex) => {
    const key = `${categoryIndex}-${questionIndex}`;
    setOpenItems(prev => ({
      ...prev,
      [key]: !prev[key]
    }));
  };

  return (
    <div className="min-h-screen bg-[#FFFDF9]">
      <Header />
      <SEO title="Haeufige Fragen zu Mobbing & Cybermobbing" schema={FAQ_SCHEMA} description="Antworten zu: Was ist Mobbing? Wie laeuft die Meldung ab? Wie sicher sind meine Daten? Alles ueber mobbing-melden.de auf einen Blick." path="/faq" />
      
      <main className="px-6 md:px-12 lg:px-24 py-12">
        <div className="max-w-4xl mx-auto">
          {/* Hero */}
          <div className="text-center mb-12">
            <div className="w-20 h-20 bg-[#BDE0FE]/30 rounded-full flex items-center justify-center mx-auto mb-6">
              <HelpCircle className="w-10 h-10 text-[#1A365D]" />
            </div>
            <h1 className="text-3xl md:text-4xl font-bold text-[#2D3748] mb-4" style={{ fontFamily: 'Nunito' }}>
              Häufige Fragen (FAQ)
            </h1>
            <p className="text-lg text-[#718096] max-w-2xl mx-auto">
              Hier findest du Antworten auf die wichtigsten Fragen. 
              Wenn deine Frage nicht dabei ist, kannst du uns gerne kontaktieren!
            </p>
          </div>

          {/* FAQ Categories */}
          {FAQ_ITEMS.map((category, categoryIndex) => {
            const Icon = category.icon;
            return (
              <div key={categoryIndex} className="mb-8">
                <div className="flex items-center gap-3 mb-4">
                  <div 
                    className="w-10 h-10 rounded-xl flex items-center justify-center"
                    style={{ backgroundColor: `${category.color}30` }}
                  >
                    <Icon className="w-5 h-5" style={{ color: category.color }} />
                  </div>
                  <h2 className="text-xl font-bold text-[#2D3748]" style={{ fontFamily: 'Nunito' }}>
                    {category.category}
                  </h2>
                </div>
                
                <div className="space-y-3">
                  {category.questions.map((item, questionIndex) => {
                    const key = `${categoryIndex}-${questionIndex}`;
                    const isOpen = openItems[key];
                    
                    return (
                      <div 
                        key={questionIndex}
                        className="bg-white rounded-xl border border-gray-100 overflow-hidden"
                      >
                        <button
                          onClick={() => toggleItem(categoryIndex, questionIndex)}
                          className="w-full flex items-center justify-between p-5 text-left hover:bg-gray-50 transition-colors"
                        >
                          <span className="font-medium text-[#2D3748] pr-4">{item.q}</span>
                          {isOpen ? (
                            <ChevronUp className="w-5 h-5 text-[#A0AEC0] flex-shrink-0" />
                          ) : (
                            <ChevronDown className="w-5 h-5 text-[#A0AEC0] flex-shrink-0" />
                          )}
                        </button>
                        {isOpen && (
                          <div className="px-5 pb-5 pt-0">
                            <p className="text-[#718096] leading-relaxed">{item.a}</p>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })}

          {/* Still have questions */}
          <div className="bg-gradient-to-br from-[#E6FFFA] to-[#FFF5F7] rounded-2xl p-8 text-center">
            <h3 className="text-xl font-bold text-[#2D3748] mb-3" style={{ fontFamily: 'Nunito' }}>
              Noch Fragen?
            </h3>
            <p className="text-[#718096] mb-6">
              Wenn du weitere Fragen hast, kannst du uns über das Meldeformular kontaktieren 
              oder die Notfall-Hotlines anrufen.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link 
                to="/melden"
                className="inline-flex items-center justify-center gap-2 bg-[#A0E8AF] text-[#1A4731] font-bold px-6 py-3 rounded-full hover:bg-[#86D696] transition-all"
              >
                Nachricht schreiben
              </Link>
              <Link 
                to="/notfall"
                className="inline-flex items-center justify-center gap-2 border-2 border-[#BDE0FE] text-[#1A365D] font-bold px-6 py-3 rounded-full hover:bg-[#BDE0FE] transition-all"
              >
                Notfall-Hotlines
              </Link>
            </div>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
}
